/**
 * Lesson Score Card (INT-CMP-008)
 * Displays lesson completion results with score, new high score indicator,
 * and next review date.
 *
 * Spec: docs/specs/mobile/lessons.mobile.md § LESSON-004
 */

import React, { useMemo } from 'react';
import { TouchableOpacity } from 'react-native';
import { Box, HStack, Text, VStack } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { colors, FS_XS, FS_SM, FS_MD, FS_2XL } from '../../theme';
import type { CompletionResult } from '../../types/lesson';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

interface LessonScoreCardProps {
  result: CompletionResult;
  onContinue: () => void;
  onRetry: () => void;
}

function getScoreEmoji(score: number): string {
  if (score >= 90) return '🌟';
  if (score >= 70) return '👏';
  if (score >= 50) return '💪';
  return '📚';
}

export const LessonScoreCard = React.memo(function LessonScoreCard({
  result,
  onContinue,
  onRetry,
}: LessonScoreCardProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const scoreColor = result.score >= 70 ? '#059669' : result.score >= 50 ? '#D97706' : '#DC2626';

  // Literal keys only — the score band picks the key, `t` is never called
  // with a computed string.
  const scoreMessage =
    result.score >= 90
      ? t('lessons.scoreCard.excellent')
      : result.score >= 70
        ? t('lessons.scoreCard.great')
        : result.score >= 50
          ? t('lessons.scoreCard.good')
          : t('lessons.scoreCard.keepPracticing');

  // Intentional: Date.now() is needed to calculate days until review
  /* eslint-disable react-hooks/purity */
  const daysUntilReview = useMemo(() => {
    const nextReview = new Date(result.nextReviewDate);
    const days = Math.ceil((nextReview.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    // A freshly-computed schedule is always >= 1 day out; clamp so device
    // clock skew can never render "in 0 days".
    return Math.max(1, days);
  }, [result.nextReviewDate]);
  /* eslint-enable react-hooks/purity */

  return (
    <Box
      p="$6"
      rounded="$2xl"
      style={{ backgroundColor: theme.card, borderWidth: 1, borderColor: theme.border }}
    >
      <VStack space="lg" alignItems="center">
        {/* Score display */}
        <Text
          allowFontScaling={false}
          accessible={false}
          importantForAccessibility="no"
          style={{ fontSize: 48 }}
        >
          {getScoreEmoji(result.score)}
        </Text>
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_2XL * fontScale, fontWeight: '800', color: scoreColor, ...ffs }}
        >
          {result.score}%
        </Text>
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_MD * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
        >
          {scoreMessage}
        </Text>

        {/* New high score */}
        {result.isNewHighScore && (
          <Box px="$4" py="$2" rounded="$full" style={{ backgroundColor: '#FEF3C7' }}>
            <HStack space="xs" alignItems="center">
              <Text
                allowFontScaling={false}
                accessible={false}
                importantForAccessibility="no"
                style={{ fontSize: FS_SM * fontScale }}
              >
                🏆
              </Text>
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: '#92400E', ...ffs }}
              >
                {t('lessons.scoreCard.newHighScore')}
              </Text>
            </HStack>
          </Box>
        )}

        {/* Stats */}
        <VStack space="xs" alignItems="center">
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}
          >
            {t('lessons.scoreCard.attempt', { number: result.attemptNumber })}
          </Text>
          {result.previousBestScore != null && !result.isNewHighScore && (
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}
            >
              {t('lessons.scoreCard.best', { score: result.previousBestScore })}
            </Text>
          )}
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}
          >
            {t('lessons.scoreCard.nextReview', { count: daysUntilReview })}
          </Text>
        </VStack>

        {/* Action buttons */}
        <VStack space="sm" w="$full">
          <TouchableOpacity
            testID="lesson-score-continue"
            accessibilityLabel={t('lessons.scoreCard.continue')}
            accessibilityRole="button"
            onPress={onContinue}
            activeOpacity={0.7}
          >
            <Box
              py="$3"
              rounded="$lg"
              alignItems="center"
              style={{ backgroundColor: colors.primary }}
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: '#FFFFFF', ...ffs }}
              >
                {t('lessons.scoreCard.continue')}
              </Text>
            </Box>
          </TouchableOpacity>

          {result.score < 90 && (
            <TouchableOpacity
              testID="lesson-score-retry"
              accessibilityLabel={t('lessons.scoreCard.tryAgain')}
              accessibilityRole="button"
              onPress={onRetry}
              activeOpacity={0.7}
            >
              <Box
                py="$3"
                rounded="$lg"
                alignItems="center"
                style={{
                  backgroundColor: theme.isDark ? '#374151' : '#F3F4F6',
                  borderWidth: 1,
                  borderColor: theme.border,
                }}
              >
                <Text
                  allowFontScaling={false}
                  style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
                >
                  {t('lessons.scoreCard.tryAgain')}
                </Text>
              </Box>
            </TouchableOpacity>
          )}
        </VStack>
      </VStack>
    </Box>
  );
});
